import { useState } from 'react'
import { BrandService } from '../../client'
import { apiErrorMessage } from '../../lib/api'
import { useAuth } from '../../providers/auth/useAuth'
import Modal from '../ui/Modal'

type Props = {
  planName: string
  limit: number
  onDone: () => void
  onCancel: () => void
}

export default function TrimBrandsDialog({ planName, limit, onDone, onCancel }: Props) {
  const { account, brand, refreshAccount } = useAuth()
  const brands = account?.brands ?? []
  const excess = brands.length - limit
  const [chosen, setChosen] = useState<Set<number>>(new Set())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function toggle(id: number) {
    setChosen((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function confirm() {
    setBusy(true)
    setError(null)
    try {
      for (const id of chosen) await BrandService.archiveBrand(id)
      await refreshAccount()
      onDone()
    } catch (err) {
      setError(apiErrorMessage(err))
      setBusy(false)
    }
  }

  return (
    <Modal title={`Make room for ${planName}`} width="max-w-md" onClose={onCancel}>
      <p className="text-sm text-stone-600">
        {planName} includes {limit} brand{limit === 1 ? '' : 's'} and you have {brands.length}. Choose {excess} to
        delete. This can&rsquo;t be undone.
      </p>

      <ul className="mt-4 divide-y divide-stone-200 rounded-xl border border-stone-200">
        {brands.map((item) => {
          const current = item.id === brand?.id
          return (
            <li key={item.id}>
              <label
                className={`flex items-center gap-3 px-4 py-3 text-sm ${current ? 'text-stone-400' : 'cursor-pointer text-stone-900'}`}
              >
                <input
                  type="checkbox"
                  disabled={current}
                  checked={chosen.has(item.id)}
                  onChange={() => toggle(item.id)}
                  className="size-4 accent-purple-700"
                />
                <span className="min-w-0 flex-1 truncate">{item.name}</span>
                {current && <span className="shrink-0 text-xs">Current brand</span>}
              </label>
            </li>
          )
        })}
      </ul>

      {error && <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-700 transition hover:bg-stone-50"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={busy || chosen.size < excess}
          onClick={() => void confirm()}
          className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
        >
          {busy ? 'Deleting…' : `Delete ${chosen.size || excess} and continue`}
        </button>
      </div>
    </Modal>
  )
}
